import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Cache implementation for hashtags
const hashtagsCache = {
  // Structure to store cached results
  cache: {},
  
  // Default expiration time in milliseconds (5 minutes)
  defaultExpiration: 5 * 60 * 1000,
  
  // Tracking for rate limiting
  lastRequestTime: {},
  minimumRequestInterval: 10 * 1000, // 10 segundos mínimo entre solicitudes para la misma clave
  
  // Para deduplicación de solicitudes
  pendingRequests: {},
  
  // Get a cached result if available and not expired
  get: function(key) {
    const item = this.cache[key];
    if (!item) {
      return null;
    }
    
    // Check if cached item has expired
    if (Date.now() > item.expiry) {
      delete this.cache[key];
      return null;
    }
    
    return item.data;
  },
  
  // Check rate limits for a key
  checkRateLimit: function(key) {
    const now = Date.now();
    const lastRequest = this.lastRequestTime[key] || 0;
    
    // Si la última solicitud fue hace menos del intervalo mínimo, rechazar
    if (now - lastRequest < this.minimumRequestInterval) {
      return false;
    }
    
    // Actualizar el tiempo de la última solicitud
    this.lastRequestTime[key] = now;
    return true;
  },
  
  // Store a result in cache with custom expiration
  set: function(key, data, expirationMs) {
    const expiry = Date.now() + (expirationMs || this.defaultExpiration);
    this.cache[key] = { data, expiry };
  },
  
  // Clear a specific cache entry
  clear: function(key) {
    delete this.cache[key];
  },
  
  // Clear all cache entries
  clearAll: function() {
    this.cache = {};
  },
  
  // Clear all cache entries for a specific university
  clearByUniversity: function(university) {
    if (!university) {
      this.clearAll();
      return;
    }
    
    for (const key in this.cache) {
      if (key.includes(`university=${university}`)) {
        delete this.cache[key];
      }
    }
  },
  
  // Método para verificar si hay una solicitud pendiente
  hasPendingRequest: function(key) {
    return !!this.pendingRequests[key];
  },
  
  // Método para registrar una solicitud pendiente
  setPendingRequest: function(key, promise) {
    this.pendingRequests[key] = promise;
    // Eliminar la promesa una vez completada
    promise.finally(() => {
      delete this.pendingRequests[key];
    });
  },
  
  // Método para obtener una solicitud pendiente
  getPendingRequest: function(key) {
    return this.pendingRequests[key];
  }
};

const hashtagsService = {
  // Generate cache key for hashtag requests
  getCacheKey: function(params) {
    const paramsStr = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `trending:${paramsStr}`;
  },
  
  // Clear all hashtag cache
  invalidateHashtagsCache: function() {
    hashtagsCache.clearAll();
  },
  
  // Clear hashtag cache for a specific university
  invalidateUniversityHashtags: function(university) {
    hashtagsCache.clearByUniversity(university);
  },
  
  getTrendingHashtags: async (limit = 5, university = null, forceRefresh = false) => {
    try {
      const params = { limit };
      
      // Solo agregar universidad si es un valor válido
      if (university) {
        params.university = university;
      }
      
      // Generate cache key
      const cacheKey = hashtagsService.getCacheKey(params);
      
      // Check rate limit first if forcing refresh
      if (forceRefresh && !hashtagsCache.checkRateLimit(cacheKey)) {
        // Usar caché si está disponible o una lista vacía
        return hashtagsCache.get(cacheKey) || [];
      }
      
      // Check cache first (unless forceRefresh is true)
      if (!forceRefresh) {
        const cachedData = hashtagsCache.get(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }
      
      // Verificar si ya hay una solicitud pendiente para esta misma clave
      if (hashtagsCache.hasPendingRequest(cacheKey)) {
        // Reutilizar la promesa existente para evitar llamadas duplicadas
        return hashtagsCache.getPendingRequest(cacheKey);
      }
      
      // Crear la nueva solicitud y registrarla como pendiente
      const requestPromise = axios.get(`${API_URL}/api/hashtags/trending`, {
        params
      }).then(response => {
        // Ensure we always have an array
        const data = Array.isArray(response.data) ? response.data : [];
        
        // Cache the result for 5 minutes by default
        hashtagsCache.set(cacheKey, data);
        
        return data;
      });
      
      // Registrar como pendiente antes de devolverla
      hashtagsCache.setPendingRequest(cacheKey, requestPromise);
      
      return requestPromise;
    } catch (error) {
      throw error;
    }
  }
};

// Export hashtagsCache to allow access from other modules if needed
export { hashtagsCache };

export default hashtagsService;